import React, { FormEvent, useId, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { PlatformForm, PlatformFormField } from "./platform.content";
import { axiosInstance } from "../../utils/api";
import { getA9ErrorMessage, getA9SuccessMessage } from "../../utils/a9-envelope";
import styles from "./platform.module.scss";

type PlatformFormProps = {
  form: PlatformForm;
};

type FormValues = Record<string, string>;
type FormFieldErrors = Record<string, string>;

type MigrationRequestPayload = {
  name: string;
  email: string;
  organisation: string;
  spatialWorldLink: string;
  typeOfExperience: string;
  numberOfWorlds: string;
  assetExportStatus: string;
  targetDeadline: string;
  creatorOrInstitutionCategory: string;
  termsAndConditions: boolean;
  privacyPolicy: boolean;
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const URL_PATTERN = /^https?:\/\/.+/i;

const buildInitialValues = (fields: PlatformForm["fields"]): FormValues =>
  fields.reduce<FormValues>((values, field) => {
    values[field.id] = "";
    return values;
  }, {});

const buildInitialErrors = (fields: PlatformForm["fields"]): FormFieldErrors =>
  fields.reduce<FormFieldErrors>((errors, field) => {
    errors[field.id] = "";
    return errors;
  }, {});

const validateField = (field: PlatformFormField, value: string): string => {
  const trimmed = value.trim();

  if (!trimmed) {
    return `${field.label} is required`;
  }

  if (field.inputType === "email" && !EMAIL_PATTERN.test(trimmed)) {
    return "Email is not valid";
  }

  if (field.inputType === "url" && !URL_PATTERN.test(trimmed)) {
    return "Enter a valid URL starting with http:// or https://";
  }

  if (field.inputType === "number") {
    const parsed = Number(trimmed);
    if (!Number.isFinite(parsed) || parsed < 1) {
      return "Enter a valid number of worlds (1 or more)";
    }
  }

  return "";
};

const validateForm = (
  fields: PlatformForm["fields"],
  values: FormValues,
  agreementsAccepted: boolean,
): { fieldErrors: FormFieldErrors; agreementsError: string } => {
  const fieldErrors = fields.reduce<FormFieldErrors>((errors, field) => {
    errors[field.id] = validateField(field, values[field.id] || "");
    return errors;
  }, buildInitialErrors(fields));

  const agreementsError = agreementsAccepted
    ? ""
    : "You must agree to the Terms and Conditions and Privacy Policy";

  return { fieldErrors, agreementsError };
};

const hasValidationErrors = (fieldErrors: FormFieldErrors, agreementsError: string): boolean =>
  Object.values(fieldErrors).some((error) => error.length > 0) || agreementsError.length > 0;

const buildMigrationPayload = (
  values: FormValues,
  agreementsAccepted: boolean,
): MigrationRequestPayload => ({
  name: values.name?.trim() || "",
  email: values.email?.trim() || "",
  organisation: values.organisation?.trim() || "",
  spatialWorldLink: values.spatialWorldLink?.trim() || "",
  typeOfExperience: values.typeOfExperience?.trim() || "",
  numberOfWorlds: values.numberOfWorlds?.trim() || "",
  assetExportStatus: values.assetExportStatus?.trim() || "",
  targetDeadline: values.targetDeadline?.trim() || "",
  creatorOrInstitutionCategory: values.creatorOrInstitutionCategory?.trim() || "",
  termsAndConditions: agreementsAccepted,
  privacyPolicy: agreementsAccepted,
});

export const PlatformMigrationForm = ({ form }: PlatformFormProps) => {
  const formId = useId();
  const [values, setValues] = useState<FormValues>(() => buildInitialValues(form.fields));
  const [fieldErrors, setFieldErrors] = useState<FormFieldErrors>(() => buildInitialErrors(form.fields));
  const [agreementsAccepted, setAgreementsAccepted] = useState(false);
  const [agreementsError, setAgreementsError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const canSubmit = agreementsAccepted && !isSubmitting;

  const onFieldChange = (fieldId: string, value: string) => {
    setValues((current) => ({ ...current, [fieldId]: value }));
    setFieldErrors((current) => (
      current[fieldId] ? { ...current, [fieldId]: "" } : current
    ));
  };

  const onAgreementsChange = (checked: boolean) => {
    setAgreementsAccepted(checked);
    if (checked) {
      setAgreementsError("");
    }
  };

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canSubmit) {
      return;
    }

    const validation = validateForm(form.fields, values, agreementsAccepted);
    setFieldErrors(validation.fieldErrors);
    setAgreementsError(validation.agreementsError);

    if (hasValidationErrors(validation.fieldErrors, validation.agreementsError)) {
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await axiosInstance.post("/platform/migration-request", buildMigrationPayload(
        values,
        agreementsAccepted,
      ));

      if (response.data?.status) {
        toast.success(
          getA9SuccessMessage(
            response.data,
            "Verification email sent. Please check your inbox to verify your account.",
          ),
          { position: "bottom-left" },
        );
        setValues(buildInitialValues(form.fields));
        setFieldErrors(buildInitialErrors(form.fields));
        setAgreementsAccepted(false);
        setAgreementsError("");
        return;
      }

      toast.error(getA9ErrorMessage(response.data, "Failed to submit migration request."), { position: "bottom-left" });
    } catch (error: any) {
      const message = getA9ErrorMessage(error?.response?.data, error?.message || "Failed to submit migration request.");
      toast.error(message, { position: "bottom-left" });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form
      id={formId}
      className={styles.migrationForm}
      aria-labelledby={`${formId}-caption`}
      onSubmit={onSubmit}
      noValidate
    >
      <h4 id={`${formId}-caption`} className={styles.formCaption}>
        {form.caption}
      </h4>

      <div className={styles.formFields}>
        {form.fields.map((field) => {
          const fieldId = `${formId}-${field.id}`;
          const errorId = `${fieldId}-error`;
          const fieldError = fieldErrors[field.id];

          return (
            <div key={field.id} className={styles.formField}>
              <div className={styles.formFieldLabelRow}>
                <label htmlFor={fieldId} className={styles.formLabel}>
                  {field.label}
                </label>
                <span className={styles.formHintText}>{field.hint}</span>
              </div>
              <input
                id={fieldId}
                name={field.id}
                className={fieldError ? `${styles.formInput} ${styles.formInputInvalid}` : styles.formInput}
                type={field.inputType || "text"}
                value={values[field.id]}
                placeholder={field.placeholder}
                onChange={(event) => onFieldChange(field.id, event.target.value)}
                autoComplete={field.inputType === "email" ? "email" : "off"}
                disabled={isSubmitting}
                required
                aria-invalid={fieldError ? true : undefined}
                aria-describedby={fieldError ? errorId : undefined}
              />
              {fieldError ? (
                <p id={errorId} className={styles.formFieldError} role="alert">
                  {fieldError}
                </p>
              ) : null}
            </div>
          );
        })}
      </div>

      <fieldset className={styles.formLegal} disabled={isSubmitting}>
        <legend className={styles.formLegalLegend}>Agreements</legend>

        <div className={styles.formCheckboxRow}>
          <input
            type="checkbox"
            id={`${formId}-agreements`}
            name="termsAndConditions"
            className={styles.formCheckbox}
            checked={agreementsAccepted}
            onChange={(event) => onAgreementsChange(event.target.checked)}
            aria-invalid={agreementsError ? true : undefined}
            aria-describedby={agreementsError ? `${formId}-agreements-error` : undefined}
          />
          <label htmlFor={`${formId}-agreements`} className={styles.formCheckboxLabel}>
            I have read and agree to the
            {" "}
            <Link
              to="/terms-and-conditions"
              target="_blank"
              rel="noopener noreferrer"
              className={styles.formCheckboxLink}
            >
              Terms and Conditions
            </Link>
            <span className={styles.formCheckboxJoiner}>and</span>
            <Link
              to="/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              className={styles.formCheckboxLink}
            >
              Privacy Policy
            </Link>
          </label>
        </div>
        {agreementsError ? (
          <p id={`${formId}-agreements-error`} className={styles.formFieldError} role="alert">
            {agreementsError}
          </p>
        ) : null}
      </fieldset>

      <button type="submit" className={styles.formSubmit} disabled={!canSubmit}>
        {isSubmitting ? "Submitting..." : (form.submitLabel || "Submit")}
      </button>
    </form>
  );
};

export default PlatformMigrationForm;
